// The Dogs lines' text (Today's Dogs card and the Dogs page). The Worker
// decides the lines (src/lib/coming-up-core.ts buildDogs); this only words
// them. Pure. iOS mirrors it word for word in mobile/lib/today.ts
// (dogsLineText, dogsLineHref); change both.

import type { DogsBlock, DogsLine } from '@/lib/api';
import { shortDate } from '@/lib/dates';

export const TK_WEB = 'https://trips.waagewu.com';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function weekday(iso: string): string {
  return WEEKDAYS[new Date(`${iso}T12:00:00Z`).getUTCDay()];
}

/** "frank" → "Frank"; ["frank", "pepper"] → "Frank and Pepper" (the Worker's dogNames). */
export function dogNames(ids: string[]): string {
  const names = ids.map((id) => id.charAt(0).toUpperCase() + id.slice(1));
  return names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** "09:00" → "9:00". */
export function clock(t: string): string {
  return t.replace(/^0(\d:)/, '$1');
}

/** "9:00–12:00", "10:30", or null. */
export function hours(time: string | null, endTime: string | null): string | null {
  if (!time) return null;
  return endTime ? `${clock(time)}–${clock(endTime)}` : clock(time);
}

/** "Today", "Tomorrow", "Sat Oct 3"; `lower` for mid-sentence ("by tomorrow"). */
function dayLabel(iso: string, daysUntil: number, lower = false): string {
  const word = daysUntil <= 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : null;
  if (word) return lower ? word.toLowerCase() : word;
  return `${weekday(iso)} ${shortDate(iso)}`;
}

// Days from today to `iso`, from the line's own date and daysUntil.
function daysTo(line: DogsLine, iso: string): number {
  return line.daysUntil + Math.round((Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${line.date}T00:00:00Z`)) / 86_400_000);
}

export interface DogsLineText {
  title: string;
  meta: string;
  /** After the meta: the trip a stay is for (quiet), a trip an appointment falls in (warn), a dog sitting a routine out (quiet). */
  note: string | null;
  tone: 'warn' | 'quiet' | null;
}

/** One Dogs line's words. `away` = the line is DogsBlock.away (with a sitter today). */
export function dogsLineText(line: DogsLine, away = false): DogsLineText {
  const on = dayLabel(line.date, line.daysUntil);
  const forTrip = line.tripName ? `For ${line.tripName}` : null;
  switch (line.type) {
    case 'stay': {
      if (away) {
        const back = line.until ? daysTo(line, line.until) : null;
        const meta =
          back === null ? 'Today' : back <= 1 ? `Back ${dayLabel(line.until!, back, true)}` : `Until ${dayLabel(line.until!, back)}`;
        return { title: line.title, meta, note: forTrip, tone: forTrip ? 'quiet' : null };
      }
      const from = [on, line.time ? clock(line.time) : null].filter(Boolean).join(' · ');
      const meta = line.until ? `${from} → ${dayLabel(line.until, daysTo(line, line.until), true)}` : from;
      return { title: line.title, meta, note: forTrip, tone: forTrip ? 'quiet' : null };
    }
    case 'appointment': {
      // Someone has to take them while the viewer is away.
      const during = line.tripName ? `During ${line.tripName}` : null;
      const meta = [on, hours(line.time, line.endTime), line.place].filter(Boolean).join(' · ');
      return { title: line.title, meta, note: during, tone: during ? 'warn' : null };
    }
    case 'routine': {
      const day = line.daysUntil <= 1 ? on : weekday(line.date);
      const meta = [day, hours(line.time, line.endTime), line.place].filter(Boolean).join(' · ');
      const skip = line.skipping?.length ? line.skipping : null;
      const skips = skip ? `${dogNames(skip)} skip${skip.length === 1 ? 's' : ''}` : null;
      const note = skips ? (line.dogs?.length ? `${dogNames(line.dogs)} only (${skips})` : skips) : null;
      return { title: line.title, meta, note, tone: note ? 'quiet' : null };
    }
    case 'care':
    default: {
      const title = line.tripName ? `${line.tripName}: ${line.title}` : line.title;
      const meta = line.daysUntil > 0 ? `By ${dayLabel(line.date, line.daysUntil, true)}` : 'Due now';
      return { title, meta, note: null, tone: null };
    }
  }
}

/** Where a line opens: the plan in Plans, or (care) the trip's to-dos in Tripper Keeper's map view. */
export function dogsLineHref(line: DogsLine): { href: string; external: boolean } {
  if (line.type === 'care' && line.tripId) {
    return { href: `${TK_WEB}/trip/${encodeURIComponent(line.tripId)}/map-mode`, external: true };
  }
  return { href: line.reservationId ? `/plans#${line.reservationId}` : '/plans', external: false };
}

/** Anything to show: the dogs are away today or something is next. */
export function hasDogs(dogs: DogsBlock | null | undefined): dogs is DogsBlock {
  return Boolean(dogs && (dogs.away || dogs.next.length));
}
